import type { PatientUser } from '~/shared/types'
import { ISO_DATE_FORMAT, formatApiDate } from '~/shared/lib/date'
import type { FC } from 'react'
import { DateAndRelativeDateInput, FFInput, PriorityToggle } from '~/shared/ui'
import {
  useGroupOptions,
  UserGroupSelect,
  type AssigneeGroupListItem,
} from '~/entities/assignee-group'
import { Box, InputAdornment } from '@mui/material'
import { memo } from 'react'
import { useSelector } from 'react-redux'
import type { TaskFormDraft } from './taskTypes'

interface InlineTaskEditorProps {
  task: TaskFormDraft
  onChange: (task: TaskFormDraft) => void
  disabled?: boolean
  patient: PatientUser
  canAssignToPatient?: boolean
  autoFocus?: boolean // If true, will autofocus the first input field.
}

const InlineTaskEditor: FC<InlineTaskEditorProps> = props => {
  const { task, disabled = false, canAssignToPatient = true, patient, autoFocus } = props
  const providers = useSelector(state => state.providers)
  const assigneeGroups = useSelector(state => state.assigneeGroups)
  let isTaskAssigneeGroupProvider: AssigneeGroupListItem | undefined = providers.find(
    provider => provider.assignee_group?.id === task.owner_group,
  )?.assignee_group
  isTaskAssigneeGroupProvider ??= assigneeGroups.find(group => group.id === task.owner_group)

  const enablePriorityToggle = isTaskAssigneeGroupProvider

  const { selectedCaseOwnerGroupOption, flatOptions, groupedOptions } = useGroupOptions(
    task.owner_group ?? null,
    canAssignToPatient,
    patient,
    patient.person.care_team,
    disabled,
  )

  return (
    <div>
      <div>
        <FFInput
          placeholder="Task Title"
          value={task.title}
          fullWidth
          multiline
          onChange={e => props.onChange({ ...props.task, title: e.target.value })}
          disabled={disabled}
          slotProps={{
            htmlInput: {
              maxLength: 255,
            },
            input: {
              autoFocus,
              endAdornment: (
                <InputAdornment
                  color=""
                  position="end"
                >{`${task.title.length}/255`}</InputAdornment>
              ),
            },
          }}
        />
      </div>
      <Box sx={{ display: 'flex', ml: -1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', pl: 1, mt: 1, flex: 1, minWidth: 275 }}>
          <DateAndRelativeDateInput
            split
            value={formatApiDate(task.due_date, ISO_DATE_FORMAT) ?? ''}
            onChange={due_date => props.onChange({ ...props.task, due_date })}
            disabled={disabled}
          />
        </Box>
        <Box sx={{ display: 'flex', pl: 1, mt: 1, flex: 1, minWidth: 275 }}>
          {
            <UserGroupSelect
              selectedValue={selectedCaseOwnerGroupOption ?? null}
              options={flatOptions}
              groupedOptions={groupedOptions}
              onChange={ownerGroupOption => {
                const owner_group = ownerGroupOption.value
                if (owner_group) props.onChange({ ...props.task, owner_group })
              }}
              patient={patient}
              fullWidth
              disabled={props.disabled}
            />
          }
          {enablePriorityToggle ? (
            <PriorityToggle
              priority={task.priority}
              onChange={priority => props.onChange({ ...props.task, priority })}
            />
          ) : null}
        </Box>
      </Box>
    </div>
  )
}

export default memo(InlineTaskEditor)
